/**
 * Deferred drawer filters for Etnia PLP.
 *
 * Behavior:
 * - Checking/unchecking filters OR clicking "Clear all" inside the drawer only
 *   updates the preview (button count + disabled options). The grid is NOT updated.
 * - The grid updates only when the user clicks "See results".
 * - Filter removals from outside the drawer (toolbar chips, external clear all)
 *   apply immediately and sync the drawer checkboxes.
 */

function getUnisexGenderInput(root = document) {
  return (
    root.querySelector('input[type="checkbox"][data-gender-unisex]') ||
    [...root.querySelectorAll('input[type="checkbox"][name*="gender"]')].find(
      (input) => input.dataset.label?.toLowerCase() === 'unisex'
    ) ||
    null
  );
}

function isGenderUnisexInput(input) {
  return (
    input instanceof HTMLInputElement &&
    (input.hasAttribute('data-gender-unisex') || input.dataset.label?.toLowerCase() === 'unisex')
  );
}

function isManOrWomanGenderInput(input) {
  const label = input.dataset.label?.toLowerCase();
  return label === 'man' || label === 'woman' || label === 'men' || label === 'women';
}

function getGenderValues(params, genderKey) {
  return params instanceof URLSearchParams ? params.getAll(genderKey) : [];
}

function applyUnisexGenderParams(params, root) {
  const allKeys = Array.from(new Set(params.keys()));
  const genderKey = allKeys.find((k) => k.toLowerCase().includes('gender'));
  if (!genderKey) return;

  const unisexInput = getUnisexGenderInput(root);
  const unisexValue = unisexInput?.value;
  if (!unisexValue) return;

  const values = getGenderValues(params, genderKey);
  const hasUnisex = values.includes(unisexValue);
  const manOrWomanValues = [...root.querySelectorAll(`input[type="checkbox"][name="${CSS.escape(genderKey)}"]`)]
    .filter((input) => isManOrWomanGenderInput(input))
    .map((input) => input.value);
  const hasManOrWomanSelected = values.some((value) => manOrWomanValues.includes(value));

  if (hasManOrWomanSelected && !hasUnisex) {
    params.append(genderKey, unisexValue);
  } else if (!hasManOrWomanSelected && hasUnisex) {
    params.delete(genderKey);
  }
}

function clearDrawerCheckboxes(drawer) {
  drawer.querySelectorAll('.facets__filters-wrapper input[type="checkbox"]').forEach((input) => {
    input.checked = false;
    input.disabled = false;
    input.closest('li')?.classList.remove('facets__inputs-list-item--disabled');
  });
  drawer.querySelectorAll('.facets__filters-wrapper input[type="number"]').forEach((input) => {
    input.value = '';
  });
}

/**
 * Syncs drawer checkbox and number inputs to match the filter state
 * encoded in `url`. Uses the URL directly — avoids reading stale form state.
 */
function syncDrawerToUrl(drawer, url) {
  const urlParams = new URL(url, window.location.origin).searchParams;
  drawer.querySelectorAll('.facets__filters-wrapper input[type="checkbox"]').forEach((input) => {
    if (isGenderUnisexInput(input)) {
      input.checked = false;
      return;
    }
    input.checked = urlParams.getAll(input.name).includes(input.value);
  });
  drawer.querySelectorAll('.facets__filters-wrapper input[type="number"]').forEach((input) => {
    input.value = urlParams.get(input.name) ?? '';
  });
  refreshGenderFacetSummaries(drawer);
}

/** Rebuild gender facet status bubbles without the hidden Unisex input. */
function refreshGenderFacetSummaries(drawer) {
  drawer.querySelectorAll('facet-inputs-component').forEach((facetInputs) => {
    const checkedInputs = [...facetInputs.querySelectorAll('input[type="checkbox"]')].filter(
      (input) => input.checked && !isGenderUnisexInput(input)
    );
    const statusComponent = facetInputs.closest('details')?.querySelector('facet-status-component');
    if (statusComponent && typeof statusComponent.updateListSummary === 'function') {
      statusComponent.updateListSummary(checkedInputs);
    }
  });
}

/**
 * Mirrors the Unisex-injection logic from custom-plp-filters.js so that the
 * drawer preview count always matches what the actual grid will show.
 * When Man/Woman is selected without Unisex → append Unisex.
 * When only Unisex remains → strip it (gender deselected).
 */
function injectUnisexGender(params, drawerForm) {
  applyUnisexGenderParams(params, drawerForm);
}

async function previewDrawerFilters(drawerForm) {
  const sectionId = drawerForm.getAttribute('section-id');
  const form = drawerForm.querySelector('form');
  if (!sectionId || !form) return;

  const params = new URLSearchParams(new FormData(form));
  params.delete('page');
  injectUnisexGender(params, drawerForm);

  const previewUrl = new URL(window.location.pathname, window.location.origin);
  for (const [key, value] of params) previewUrl.searchParams.append(key, value);
  previewUrl.searchParams.set('section_id', sectionId);

  let html;
  try {
    const res = await fetch(previewUrl.toString(), { signal: AbortSignal.timeout(5000) });
    if (!res.ok) return;
    html = await res.text();
    if (!html) return;
  } catch {
    return;
  }

  const doc = new DOMParser().parseFromString(html, 'text/html');
  const drawer = document.getElementById('filters-drawer');
  if (!drawer) return;

  // Update "See X items" button count
  const newBtn = doc.querySelector('.facets__see-results');
  const currentBtn = drawer.querySelector('.facets__see-results');
  if (newBtn && currentBtn) {
    currentBtn.innerHTML = newBtn.innerHTML;
  }

  // Sync filter option disabled/available states
  doc.querySelectorAll('.facets__filters-wrapper input').forEach((newInput) => {
    const name = CSS.escape(newInput.name);
    const value = CSS.escape(newInput.value);
    const currentInput = drawer.querySelector(
      `.facets__filters-wrapper input[name="${name}"][value="${value}"]`
    );
    if (!currentInput) return;

    currentInput.disabled = newInput.disabled;
    currentInput.closest('li')?.classList.toggle('facets__inputs-list-item--disabled', newInput.disabled);
  });
}

function initDrawerDeferredFilters() {
  const drawer = document.getElementById('filters-drawer');
  if (!drawer) return;

  const drawerForm = drawer.querySelector('facets-form-component');
  if (!drawerForm?.updateFilters || drawerForm.dataset.deferInit) return;

  drawerForm.dataset.deferInit = 'true';
  drawerForm._deferredUpdate = drawerForm.updateFilters.bind(drawerForm);
  drawerForm._deferredUpdateByURL = drawerForm.updateFiltersByURL.bind(drawerForm);

  // Intercept checkbox changes: preview only, no grid update
  drawerForm.updateFilters = function () {
    this.dataset.pendingFilters = 'true';
    delete this.dataset.pendingUrl;
    previewDrawerFilters(this);
  };

  // Intercept URL-based filter changes (chip removal, clear all)
  drawerForm.updateFiltersByURL = function (url) {
    const isDrawerOpen = !!drawer.querySelector('dialog[open]');

    if (!isDrawerOpen) {
      syncDrawerToUrl(drawer, url);
      previewDrawerFilters(this);
      this._deferredUpdateByURL(url);
      return;
    }

    // Drawer open: defer grid update, update visuals only
    this.dataset.pendingFilters = 'true';
    this.dataset.pendingUrl = url;
    clearDrawerCheckboxes(drawer);
    previewDrawerFilters(this);
  };

  const dialog = drawer.querySelector('dialog');
  if (dialog && !drawer.dataset.drawerUrlSyncInit) {
    drawer.dataset.drawerUrlSyncInit = 'true';
    new MutationObserver(() => {
      if (dialog.hasAttribute('open')) {
        syncDrawerToUrl(drawer, window.location.href);
      }
    }).observe(dialog, { attributes: true, attributeFilter: ['open'] });
  }
}

customElements.whenDefined('facets-form-component').then(initDrawerDeferredFilters);

// Re-apply after Shopify section re-renders
document.addEventListener('shopify:section:load', initDrawerDeferredFilters);

// Apply to grid when "See results" is clicked.
// We defer the actual filter update until AFTER the drawer finishes its close animation.
// Triggering a section re-render while the dialog is still animating causes morph to strip
// the `dialog-closing` CSS class, which makes the browser play the opening animation briefly
// before the dialog finally closes.
document.addEventListener('click', (event) => {
  const btn = event.target.closest('.facets__see-results');
  if (!btn) return;

  const drawer = btn.closest('#filters-drawer');
  if (!drawer) return;

  const drawerForm = drawer.querySelector('facets-form-component');
  if (!drawerForm) return;
  if (drawerForm.dataset.pendingFilters !== 'true') return;

  const pendingUrl = drawerForm.dataset.pendingUrl;
  delete drawerForm.dataset.pendingFilters;
  delete drawerForm.dataset.pendingUrl;

  // Wait for dialog:close (fired by Horizon after animation + dialog.close()),
  // then trigger the grid update. This prevents the section re-render from
  // conflicting with the close animation.
  drawer.addEventListener(
    'dialog:close',
    () => {
      if (pendingUrl) {
        drawerForm._deferredUpdateByURL(pendingUrl);
      } else {
        drawerForm._deferredUpdate();
      }
    },
    { once: true }
  );
});

// Sync drawer when filters change from sources that bypass our override
// (horizontal filter bar checkboxes, direct URL navigation).
// Uses window.location.href because pushState always runs before this event fires.
document.addEventListener('filter:update', (event) => {
  const drawer = document.getElementById('filters-drawer');
  if (!drawer) return;

  const drawerForm = drawer.querySelector('facets-form-component');
  // Skip if user is mid-interaction inside the drawer
  if (drawerForm?.dataset.pendingFilters === 'true') return;

  syncDrawerToUrl(drawer, window.location.href);

  if (drawerForm) previewDrawerFilters(drawerForm);
});

// "Clear all" inside drawer: apply immediately, reset explore radios and close
document.addEventListener('click', (event) => {
  if (!event.target.closest('.facets__clear-all')) return;

  const drawer = document.getElementById('filters-drawer');
  if (!drawer?.querySelector('dialog[open]')) return;

  const drawerForm = drawer.querySelector('facets-form-component');
  if (!drawerForm?._deferredUpdateByURL) return;

  event.preventDefault();
  event.stopImmediatePropagation();

  const cleanUrl = new URL(window.location.href);
  [...cleanUrl.searchParams.keys()]
    .filter((k) => k.startsWith('filter.') || k === 'page')
    .forEach((k) => cleanUrl.searchParams.delete(k));

  delete drawerForm.dataset.pendingFilters;
  delete drawerForm.dataset.pendingUrl;

  // Reset injected elements in drawer
  drawer.querySelectorAll('.explore-filter-radio, .popular-filter-radio').forEach((r) => {
    r.checked = false;
    r.dataset.active = 'false';
  });
  drawer.querySelectorAll('[data-popular-chip]').forEach((chip) => {
    chip.classList.remove('is-active');
  });

  // Reset source element so next injection starts clean
  const source = document.getElementById('popular-filters-source');
  source?.querySelectorAll('[data-popular-chip]').forEach((chip) => {
    chip.classList.remove('is-active');
    const radio = chip.querySelector('input[type="radio"]');
    if (radio) radio.checked = false;
  });

  // Defer the grid update until after the close animation completes,
  // same as "Show X items" — prevents morph from stripping dialog-closing
  // mid-animation and triggering a spurious opening animation.
  drawer.addEventListener('dialog:close', () => {
    drawerForm._deferredUpdateByURL(cleanUrl.toString());
  }, { once: true });

  if (typeof drawer.closeDialog === 'function') {
    drawer.closeDialog();
  } else {
    drawer.querySelector('dialog')?.close();
  }
}, true);

// ─── Explore Filters ──────────────────────────────────────────────────────────

function initExploreFilters() {
  const template = document.getElementById('explore-filters-template');
  if (!template) return;

  const drawer = document.getElementById('filters-drawer');
  if (!drawer) return;

  const dialog = drawer.querySelector('dialog');
  if (!dialog) return;

  if (drawer.dataset.exploreFiltersInit) return;
  drawer.dataset.exploreFiltersInit = 'true';

  function findCheckboxByLabel(labelText) {
    const needle = labelText.trim().toLowerCase();
    for (const input of drawer.querySelectorAll('.facets__filters-wrapper input[type="checkbox"]')) {
      if (input.value.trim().toLowerCase() === needle) return input;
      const label =
        input.closest('label') ??
        (input.id ? drawer.querySelector(`label[for="${CSS.escape(input.id)}"]`) : null);
      if (label) {
        const el = label.querySelector('.checkbox__label-text');
        const text = (el ? el.textContent : label.textContent).trim().toLowerCase();
        if (text === needle) return input;
      }
    }
    return null;
  }

  function syncRadiosToCheckboxes() {
    drawer.querySelectorAll('.explore-filter-radio').forEach((radio) => {
      const active = findCheckboxByLabel(radio.dataset.filterLabel ?? '')?.checked ?? false;
      radio.checked = active;
      radio.dataset.active = String(active);
    });
  }

  function injectExploreSection() {
    if (drawer.querySelector('[data-explore-filters]')) {
      syncRadiosToCheckboxes();
      return;
    }

    const filtersWrapper = drawer.querySelector('.facets__filters-wrapper');
    if (!filtersWrapper) return;

    const clone = template.content.cloneNode(true);
    const firstPanel = filtersWrapper.querySelector('details.facets__panel');
    firstPanel
      ? filtersWrapper.insertBefore(clone, firstPanel)
      : filtersWrapper.prepend(clone);

    const explore = drawer.querySelector('[data-explore-filters]');
    if (!explore) return;

    syncRadiosToCheckboxes();

    const radios = explore.querySelectorAll('.explore-filter-radio');
    radios.forEach((radio) => {
      radio.addEventListener('click', () => {
        const wasActive = radio.dataset.active === 'true';

        explore.querySelectorAll('.explore-filter-radio[data-active="true"]').forEach((prev) => {
          prev.checked = false;
          prev.dataset.active = 'false';
          const cb = findCheckboxByLabel(prev.dataset.filterLabel ?? '');
          if (cb) cb.checked = false;
        });

        if (!wasActive) {
          radio.dataset.active = 'true';
          const cb = findCheckboxByLabel(radio.dataset.filterLabel ?? '');
          if (cb) cb.checked = true;
        }

        const drawerForm = drawer.querySelector('facets-form-component');
        if (drawerForm) {
          drawerForm.dataset.pendingFilters = 'true';
          delete drawerForm.dataset.pendingUrl;
          previewDrawerFilters(drawerForm);
        }
      });
    });
  }

  new MutationObserver(() => {
    if (dialog.hasAttribute('open')) {
      injectExploreSection();
    } else {
      drawer.querySelector('[data-explore-filters]')?.remove();
    }
  }).observe(dialog, { attributes: true, attributeFilter: ['open'] });

  document.addEventListener('shopify:section:load', () => {
    if (dialog.hasAttribute('open')) injectExploreSection();
  });

  document.addEventListener('filter:update', () => {
    if (dialog.hasAttribute('open')) syncRadiosToCheckboxes();
  });
}

customElements.whenDefined('facets-form-component').then(initExploreFilters);
document.addEventListener('shopify:section:load', initExploreFilters);

// ─── Popular Filters ──────────────────────────────────────────────────────────

function initPopularFilters() {
  const source = document.getElementById('popular-filters-source');
  if (!source) return;

  const popularEl = source.querySelector('[data-popular-filters]');
  if (!popularEl) return;

  const drawer = document.getElementById('filters-drawer');
  if (!drawer) return;

  const dialog = drawer.querySelector('dialog');
  if (!dialog) return;

  if (drawer.dataset.popularFiltersInit) return;
  drawer.dataset.popularFiltersInit = 'true';

  function syncActiveStates() {
    const current = new URLSearchParams(window.location.search);
    drawer.querySelectorAll('[data-popular-chip]').forEach((chip) => {
      const chipParams = new URLSearchParams(chip.dataset.params);
      const allMatch = [...chipParams].every(([k, v]) => current.getAll(k).includes(v));
      chip.classList.toggle('is-active', allMatch);
      const radio = chip.querySelector('input[type="radio"]');
      if (radio) radio.checked = allMatch;
    });
  }

  function findDrawerCheckbox(key, value) {
    const keyLower = key.toLowerCase();
    const valueLower = value.toLowerCase();
    return [...drawer.querySelectorAll('.facets__filters-wrapper input[type="checkbox"]')]
      .find((input) => input.name.toLowerCase() === keyLower && input.value.toLowerCase() === valueLower) ?? null;
  }

  function syncDisabledStates() {
    drawer.querySelectorAll('[data-popular-chip]').forEach((chip) => {
      const chipParams = new URLSearchParams(chip.dataset.params);
      const allAvailable = [...chipParams].every(([key, value]) => {
        const cb = findDrawerCheckbox(key, value);
        return cb && !cb.disabled;
      });
      chip.classList.toggle('is-unavailable', !allAvailable);
    });
  }

  function injectPopularFilters() {
    if (drawer.querySelector('[data-popular-filters]')) {
      syncActiveStates();
      return;
    }

    const filtersWrapper = drawer.querySelector('.facets__filters-wrapper');
    if (!filtersWrapper) return;

    filtersWrapper.prepend(popularEl.cloneNode(true));
    syncActiveStates();
    syncDisabledStates();

    const injected = drawer.querySelector('[data-popular-filters]');
    if (!injected) return;

    injected.querySelectorAll('[data-popular-chip]').forEach((chip) => {
      chip.addEventListener('click', async (e) => {
        e.preventDefault();
        e.stopPropagation();

        if (chip.classList.contains('is-unavailable')) return;

        const isActive = chip.classList.contains('is-active');

        injected.querySelectorAll('[data-popular-chip]').forEach((other) => {
          const otherParams = new URLSearchParams(other.dataset.params);
          otherParams.forEach((value, key) => {
            const cb = findDrawerCheckbox(key, value);
            if (cb) cb.checked = false;
          });
          other.classList.remove('is-active');
          const otherRadio = other.querySelector('input[type="radio"]');
          if (otherRadio) otherRadio.checked = false;
        });

        if (!isActive) {
          const chipParams = new URLSearchParams(chip.dataset.params);
          chipParams.forEach((value, key) => {
            const cb = findDrawerCheckbox(key, value);
            if (cb) cb.checked = true;
          });
          chip.classList.add('is-active');
          const radio = chip.querySelector('input[type="radio"]');
          if (radio) radio.checked = true;
        }

        const drawerForm = drawer.querySelector('facets-form-component');
        if (drawerForm) {
          drawerForm.dataset.pendingFilters = 'true';
          delete drawerForm.dataset.pendingUrl;
          await previewDrawerFilters(drawerForm);
          syncDisabledStates();
        }
      });
    });
  }

  new MutationObserver(() => {
    if (dialog.hasAttribute('open')) {
      injectPopularFilters();
    } else {
      drawer.querySelector('[data-popular-filters]')?.remove();
    }
  }).observe(dialog, { attributes: true, attributeFilter: ['open'] });

  document.addEventListener('shopify:section:load', () => {
    if (dialog.hasAttribute('open')) injectPopularFilters();
  });

  document.addEventListener('filter:update', () => {
    if (dialog.hasAttribute('open')) {
      syncActiveStates();
      syncDisabledStates();
    }
  });
}

customElements.whenDefined('facets-form-component').then(initPopularFilters);
document.addEventListener('shopify:section:load', initPopularFilters);

// ─── Swatch Label Tooltips ────────────────────────────────────────────────────

function updateSwatchTooltips() {
  const drawer = document.getElementById('filters-drawer');
  if (!drawer) return;

  drawer.querySelectorAll('.variant-option__button-label--has-swatch').forEach((btn) => {
    const label = btn.querySelector('.facets__swatch-label');
    if (!label) return;

    if (label.scrollWidth > label.clientWidth) {
      btn.dataset.tooltip = label.textContent.trim();
    } else {
      delete btn.dataset.tooltip;
    }
  });
}

function initSwatchTooltips() {
  const drawer = document.getElementById('filters-drawer');
  if (!drawer) return;

  const dialog = drawer.querySelector('dialog');
  if (!dialog) return;

  if (drawer.dataset.swatchTooltipsInit) return;
  drawer.dataset.swatchTooltipsInit = 'true';

  new MutationObserver(() => {
    if (dialog.hasAttribute('open')) {
      requestAnimationFrame(updateSwatchTooltips);
    }
  }).observe(dialog, { attributes: true, attributeFilter: ['open'] });
}

customElements.whenDefined('facets-form-component').then(initSwatchTooltips);
document.addEventListener('shopify:section:load', initSwatchTooltips);